/**
 * Marqueur « Lire plus » (équivalent du `<!--more-->` de WordPress) pour les
 * champs Remarques / Notes publiques.
 *
 * ProseMirror/TipTap supprime les commentaires HTML, on utilise donc un nœud
 * dédié sérialisé en `<div data-read-more="true" class="read-more-tag"></div>`.
 * Le rendu (front-public + prévisu) coupe le HTML à ce marqueur : la partie
 * avant est toujours visible, la suite est révélée par un bouton « Lire plus ».
 */
export const READ_MORE_ATTR = "data-read-more";

/** HTML sérialisé du nœud (doit correspondre au `renderHTML` de l'extension). */
export const READ_MORE_HTML = `<div ${READ_MORE_ATTR}="true" class="read-more-tag"></div>`;

// Un `<div>` (ou `<hr>` par tolérance) portant l'attribut data-read-more, quel
// que soit l'ordre des attributs. Le nœud est un atome (pas de contenu interne).
const MARKER_RE_SOURCE = `<(div|hr)\\b[^>]*\\b${READ_MORE_ATTR}\\b[^>]*>(?:\\s*</\\1>)?`;

/** Vrai si le HTML contient au moins un marqueur « Lire plus ». */
export function hasReadMoreMarker(html: string | null | undefined): boolean {
  if (!html) return false;
  return new RegExp(MARKER_RE_SOURCE, "i").test(html);
}

/** Supprime tous les marqueurs d'une chaîne HTML. */
export function stripReadMoreMarkers(html: string | null | undefined): string {
  if (!html) return "";
  return html.replace(new RegExp(MARKER_RE_SOURCE, "gi"), "");
}

/**
 * Coupe le HTML au PREMIER marqueur (comportement WordPress). Renvoie la partie
 * visible (`before`) et la suite masquée (`after`, `null` s'il n'y a pas de
 * marqueur). Les marqueurs résiduels sont retirés des deux morceaux.
 */
export function splitReadMore(html: string | null | undefined): {
  before: string;
  after: string | null;
} {
  if (!html) return { before: "", after: null };
  const match = html.match(new RegExp(MARKER_RE_SOURCE, "i"));
  if (!match || match.index == null) {
    return { before: stripReadMoreMarkers(html), after: null };
  }
  const before = stripReadMoreMarkers(html.slice(0, match.index));
  const after = stripReadMoreMarkers(html.slice(match.index + match[0].length));
  return { before, after: after.trim() ? after : "" };
}
